import { DisplayName } from '@uplift/utils';
import { DateObject } from '@uplift/services';
import React from 'react';

export enum ModalsEnum {
  cancelSession = 'cancelSession',
}

type Modals = Partial<{
  [ModalsEnum.cancelSession]: {
    cancelMode: 'single' | 'all' | 'manual';
    appointment: {
      id: string;
      fromDatetime: DateObject | string;
      parentId: string | null;
      providerName: DisplayName;
      isMaterialized?: boolean;
    };
  };
}>;

export type ModalsContextType = {
  modals: Modals;
  isVisible: (modalType: ModalsEnum) => boolean;
  openModal: <T extends ModalsEnum>(modalType: T, data?: Modals[T]) => void;
  getData: <T extends ModalsEnum>(modalType: T) => Modals[T] | undefined;
  closeModal: (modalType: ModalsEnum) => void;
};

const ModalsContext = React.createContext<ModalsContextType | null>(null);

export const useModalsContext = () => {
  const context = React.useContext(ModalsContext);

  if (!context) {
    throw new Error('useModalsContext must be used within a ModalsProvider.');
  }
  return context;
};

export const ModalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ modalsState, setModalsState ] = React.useState<Modals>({
    cancelSession: undefined,
  });

  return (
    <ModalsContext.Provider
      value={{
        modals: modalsState,
        isVisible: (modalType: ModalsEnum) => {
          return !!modalsState[modalType];
        },
        getData: <T extends ModalsEnum>(modalType: T): Modals[T] | undefined => {
          return modalsState[modalType];
        },
        openModal<T extends ModalsEnum>(modalType: T, modalData?: Modals[T]) {
          setModalsState((prev) => ({
            ...prev,
            [modalType]: modalData,
          }));
        },
        closeModal: (modalType: ModalsEnum) => {
          setModalsState((prev) => ({
            ...prev,
            [modalType]: undefined,
          }));
        },
      }}
    >
      {children}
    </ModalsContext.Provider>
  );
};
